import React, { useState, useContext } from 'react';
import { Button, Input, Form, message } from 'antd';
import { MailOutlined, EnvironmentOutlined, PhoneOutlined, FacebookFilled, TwitterSquareFilled, InstagramFilled, YoutubeFilled } from '@ant-design/icons';
import AnimatedSection from './AnimatedSection';
import { Context } from '../store/Context';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

const { TextArea } = Input;

const ContactSection = ({ backgroundUrl }) => {
  const { state } = useContext(Context);
  const { contactEmail, contactPhone, contactAddress, socialLinks = {} } = state;

  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'contacts'), { ...values, timestamp: new Date() });
      form.resetFields();
      message.success('Your message has been sent!');
    } catch (error) {
      console.error('Error sending contact message:', error);
      message.error('Failed to send. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialIcons = {
    facebook: <FacebookFilled />,
    twitter: <TwitterSquareFilled />,
    instagram: <InstagramFilled />,
    youtube: <YoutubeFilled />
  };

  return (
    <section
      id="contact-section"
      className="section-with-bg contact-section"
      style={{ backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : 'none' }}
    >
      <div className="section-bg-overlay" />
      <div className="section-content-wrapper container">
        <AnimatedSection className="w-full">
          <h2 className="section-title">Contact Us</h2>
          <p className="section-subtitle">
            Send us a note and we’ll get back to you as soon as we can.
          </p>
          <div className="contact-grid">
            <div className="space-y-6">
              <AnimatedSection className="contact-card">
                <h3 className="contact-card-title">Get In Touch</h3>
                <div className="space-y-4">
                  {contactAddress && (
                    <div className="contact-info-item">
                      <EnvironmentOutlined />
                      <div>
                        <h4>Headquarters</h4>
                        <p>{contactAddress}</p>
                      </div>
                    </div>
                  )}
                  {contactPhone && (
                    <div className="contact-info-item">
                      <PhoneOutlined />
                      <div>
                        <h4>Phone</h4>
                        <p>{contactPhone}</p>
                      </div>
                    </div>
                  )}
                  {contactEmail && (
                    <div className="contact-info-item">
                      <MailOutlined />
                      <div>
                        <h4>Email</h4>
                        <p>{contactEmail}</p>
                      </div>
                    </div>
                  )}
                </div>
              </AnimatedSection>
              <AnimatedSection className="contact-card">
                <h3 className="contact-card-title">Follow Us</h3>
                <div className="social-links-container">
                  {Object.entries(socialLinks).map(
                    ([platform, url]) =>
                      url &&
                      socialIcons[platform] && (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-link"
                        >
                          {socialIcons[platform]}
                        </a>
                      )
                  )}
                </div>
              </AnimatedSection>
            </div>
            <AnimatedSection className="contact-card contact-form">
              <Form
                form={form}
                onFinish={handleSubmit}
                layout="vertical"
                className="contact-form"
              >
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: 'Please enter your name' }]}
                >
                  <Input placeholder="Your name" size="large" disabled={isSubmitting} />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input placeholder="Your email" size="large" disabled={isSubmitting} />
                </Form.Item>
                <Form.Item
                  name="message"
                  label="Message"
                  rules={[{ required: true, message: 'Please enter a message' }]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Your message"
                    disabled={isSubmitting}
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={isSubmitting}
                  >
                    {isSubmitting ? 'Sending…' : 'Send Message'}
                  </Button>
                </Form.Item>
              </Form>
            </AnimatedSection>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default ContactSection;